import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import styles from "./CommunicationPublishButtons.module.css";
import { COMMUNICATION_TIMELINE_ENTRY, ICommunicationEntry } from "src/stores/CommunicationStore";
import { RootStoreContext } from "src/stores/RootStore";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import { UI } from "@wwimmo/ui";

enum ButtonType {
    Published = "published",
    Unpublished = "unpublished"
}

interface CommunicationTimelineEntryProps {
    entry: ICommunicationEntry;
    isAgendaPublishedAndProtocolfileExists?: boolean;
}

interface ButtonConfig {
    timelineEntry: COMMUNICATION_TIMELINE_ENTRY;
    allowedAssemblyStates: ASSEMBLY_STATE | ASSEMBLY_STATE[];
    type: ButtonType;
}

const buttonConfigs: ButtonConfig[] = [
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED,
        allowedAssemblyStates: ASSEMBLY_STATE.PREPARING,
        type: ButtonType.Published
    },
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED,
        allowedAssemblyStates: [ASSEMBLY_STATE.PREPARING, ASSEMBLY_STATE.DATE_PUBLISHED],
        type: ButtonType.Published
    },
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED,
        allowedAssemblyStates: [
            ASSEMBLY_STATE.PREPARING,
            ASSEMBLY_STATE.DATE_PUBLISHED,
            ASSEMBLY_STATE.AGENDA_PUBLISHED
        ],
        type: ButtonType.Published
    },
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED,
        allowedAssemblyStates: ASSEMBLY_STATE.DATE_PUBLISHED,
        type: ButtonType.Unpublished
    },
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED,
        allowedAssemblyStates: ASSEMBLY_STATE.AGENDA_PUBLISHED,
        type: ButtonType.Unpublished
    },
    {
        timelineEntry: COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED,
        allowedAssemblyStates: ASSEMBLY_STATE.PROTOCOL_PUBLISHED,
        type: ButtonType.Unpublished
    }
];

const CommunicationPublishButtonsBase: React.FC<CommunicationTimelineEntryProps> = ({
    entry,
    isAgendaPublishedAndProtocolfileExists
}) => {
    const { t } = useTranslation();
    const { assemblyStore, communicationStore } = useContext(RootStoreContext);
    const { updateAssemblyState } = assemblyStore;
    const { loadAssemblyHistory } = communicationStore;
    const assemblyState = assemblyStore.currentAssembly?.state;
    const isAgendaPublishBlocked =
        entry.state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED && !assemblyStore.agendaItemsExists;

    const onClickPublished = useCallback(
        (state: COMMUNICATION_TIMELINE_ENTRY) => async (e: React.MouseEvent) => {
            e.preventDefault();

            if (state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED && !assemblyStore.agendaItemsExists) {
                return;
            }

            const isAssemblyPreparingOrDatePublishedState =
                assemblyState === ASSEMBLY_STATE.PREPARING || assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED;

            if (assemblyState === ASSEMBLY_STATE.PROTOCOL_PUBLISHED) {
                await updateAssemblyState(1);
                communicationStore.isProtocolExists = false;
            } else if (
                assemblyState === ASSEMBLY_STATE.AGENDA_PUBLISHED &&
                state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED
            ) {
                await updateAssemblyState(5);
                if (assemblyStore.currentAssembly?.id && assemblyStore.currentAssembly?.id !== "") {
                    loadAssemblyHistory(assemblyStore.currentAssembly?.id);
                }
            } else if (
                assemblyState === ASSEMBLY_STATE.DATE_PUBLISHED &&
                state === COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED
            ) {
                await updateAssemblyState(0);
                if (assemblyStore.currentAssembly?.id && assemblyStore.currentAssembly?.id !== "") {
                    loadAssemblyHistory(assemblyStore.currentAssembly?.id);
                }
            } else if (isAssemblyPreparingOrDatePublishedState) {
                if (
                    state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED &&
                    !communicationStore.isAgendaItemFileExists
                ) {
                    if (assemblyStore.currentAssembly?.id && assemblyStore.currentAssembly?.id !== "") {
                        assemblyStore.generateAssemblyRealestatePDF(assemblyStore.currentAssembly?.id || "");
                    }
                }

                await updateAssemblyState(state === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED ? 1 : 5);
                if (assemblyStore.currentAssembly?.id && assemblyStore.currentAssembly?.id !== "") {
                    loadAssemblyHistory(assemblyStore.currentAssembly?.id);
                }
            } else if (state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED) {
                await updateAssemblyState(6);
            }
        },
        [updateAssemblyState, loadAssemblyHistory, assemblyState, communicationStore, assemblyStore]
    );

    const renderButton = (config: ButtonConfig) => {
        const { timelineEntry, allowedAssemblyStates, type } = config;

        if (assemblyState === undefined) {
            return null;
        }

        const stateMatches = Array.isArray(allowedAssemblyStates)
            ? allowedAssemblyStates.includes(assemblyState)
            : assemblyState === allowedAssemblyStates;

        if (entry.state !== timelineEntry || !stateMatches) {
            return null;
        }

        const entryTypeInteger = communicationStore.mapCommunicationStateToInteger(timelineEntry);
        const isAgendaAlreadyPublishedAndNotified =
            type === ButtonType.Published &&
            entryTypeInteger === 1 &&
            !!entry.statusChangeEntry &&
            !!entry.publishChangeEntry;

        const label = isAgendaAlreadyPublishedAndNotified
            ? t("screens.assemblies.communicationcontent.button_agenda_update")
            : t(`screens.assemblies.communicationcontent.${type}`);

        return (
            <div key={`${timelineEntry}-${type}`}>
                <UI.Button
                    className={styles.ButtonPublished}
                    label={label}
                    onClick={onClickPublished(timelineEntry)}
                    variant={type === ButtonType.Unpublished ? "outline-danger" : "primary"}
                    disabled={
                        entry.state === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED
                            ? !isAgendaPublishedAndProtocolfileExists
                            : isAgendaPublishBlocked
                    }
                />
            </div>
        );
    };

    return <React.Fragment>{buttonConfigs.map(renderButton)}</React.Fragment>;
};

export const CommunicationPublishButtons = Sentry.withProfiler(observer(CommunicationPublishButtonsBase));
